import Content from './content';
import { useDashboardTranslation } from '@/hooks';
import useTranslation from '@CS/hooks/useTranslation';
import { Popup } from '@CS/components/Popups';
import { useEffect, useMemo, useState } from 'react';

import {
  Note,
  Title,
  Arrow,
  Button,
  DateRow,
  DateInput,
  DateFields,
  DateSelect,
  DateColumn,
  DateLabel,
  FieldGroup,
  FieldLabel,
  NoteIcon,
  Stepper,
  Description,
  StepperValue,
  PopupContent,
  DeleteDateButton,
  StepperButton
} from './FamilyDetailsPopup.styles';

export type FamilyDetails = {
  adults: number;
  children: number;
  childBirthDates: string[];
};

type FamilyDetailsPopupProps = {
  value: FamilyDetails;
  onChange: (value: FamilyDetails) => void;
  onClose: () => void;
};

const MAX_MEMBERS = 8;
const ACTIVE_ICON_COLOR = '#FFBC0D';
const DISABLED_ICON_COLOR = '#9C9C9C';
const CURRENT_YEAR = new Date().getFullYear();

type DateParts = {
  month: string;
  year: string;
};

type IconProps = {
  active?: boolean;
};

const MinusIcon = ({ active }: IconProps) => (
  <svg
    width="24"
    height="4"
    viewBox="0 0 24 4"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24 2C24 3.10457 23.1312 4 22.0594 4L1.94062 4C0.868846 4 -4.68488e-08 3.10457 0 2C4.68488e-08 0.89543 0.868846 -4.82821e-08 1.94062 0L22.0594 9.06327e-07C23.1312 9.54609e-07 24 0.895431 24 2Z"
      fill={active ? ACTIVE_ICON_COLOR : DISABLED_ICON_COLOR}
    />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.6305 1.94062C13.6305 0.868846 12.7616 0 11.6898 0C10.6181 0 9.74922 0.868846 9.74922 1.94062V10.0594H1.94062C0.868846 10.0594 4.68487e-08 10.9282 0 12C-4.68487e-08 13.0718 0.868846 13.9406 1.94062 13.9406H9.74922V22.0594C9.74922 23.1312 10.6181 24 11.6898 24C12.7616 24 13.6305 23.1312 13.6305 22.0594V13.9406H22.0594C23.1312 13.9406 24 13.0718 24 12C24 10.9282 23.1312 10.0594 22.0594 10.0594H13.6305V1.94062Z"
      fill={ACTIVE_ICON_COLOR}
    />
  </svg>
);

const SelectChevronIcon = () => (
  <svg
    width="20"
    height="12"
    viewBox="0 0 20 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.99141 11.0086C9.54926 11.5664 10.4552 11.5664 11.0131 11.0086L19.5816 2.44003C20.1395 1.88218 20.1395 0.976236 19.5816 0.418387C19.0238 -0.139462 18.1178 -0.139462 17.56 0.418387L10 7.97836L2.44003 0.422849C1.88218 -0.135 0.976236 -0.135 0.418387 0.422849C-0.139462 0.980698 -0.139462 1.88664 0.418387 2.44449L8.98695 11.0131L8.99141 11.0086Z"
      fill="#292929"
    />
  </svg>
);

const NoteHandIcon = () => (
  <svg
    width="30"
    height="30"
    viewBox="0 0 30 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#family-details-note-icon)">
      <path
        d="M9.82811 3.3368C10.6305 3.27779 11.2048 3.58345 11.7374 4.16804C12.2688 3.58442 12.9377 3.35171 13.7241 3.5345C14.3431 3.67837 14.7198 4.03745 15.041 4.56603C15.8593 3.67364 17.4846 3.90822 17.8598 5.13138C17.9258 5.34673 17.9683 5.60464 18.0228 5.82773L18.4242 7.45837L19.0112 9.84114C19.119 10.2792 19.2387 10.7366 19.3363 11.1755C19.7264 11.2293 20.2188 11.2043 20.6197 11.2376C20.8276 11.2548 20.9005 11.2935 21.0868 11.3345C21.6449 11.4572 22.1242 11.613 22.618 11.9162C22.7845 12.0188 22.9443 12.1322 23.0959 12.2559C23.2032 12.345 23.3317 12.4715 23.4489 12.5412C23.792 12.5542 24.1769 12.5459 24.5225 12.5456L26.2844 12.5459C26.5881 12.5458 26.8922 12.5422 27.1956 12.5433C27.3536 12.5481 27.579 12.5528 27.6889 12.6825C27.9192 12.9541 27.7985 13.4181 27.4093 13.4441C27.2112 13.4574 27.0458 13.4578 26.8538 13.458L25.9853 13.4583L24.8264 13.4579C24.6098 13.4578 24.3446 13.4644 24.1318 13.45C24.1746 13.5128 24.2037 13.5714 24.2383 13.6384C24.8205 14.765 24.8775 15.9624 24.4952 17.1586C24.6795 17.1465 24.9075 17.1521 25.096 17.1519L26.1254 17.151C26.5538 17.1509 26.983 17.1413 27.4105 17.1652C27.9384 17.1947 27.929 17.9941 27.4283 18.0509C27.2605 18.0712 27.0878 18.0699 26.9187 18.0674C25.9921 18.054 25.058 18.0827 24.1324 18.0697C23.9878 18.2655 23.8727 18.5236 23.7102 18.7366C23.1859 19.4232 22.6059 19.9541 21.9019 20.444C21.6666 20.6043 21.4281 20.7598 21.1865 20.9106C21.079 20.9782 20.7306 21.1752 20.6964 21.2724C20.6376 21.4398 20.3765 22.0058 20.3676 22.1439C20.3644 22.1954 20.7376 22.857 20.7787 22.9327L22.1276 25.4332L22.5343 26.1808C22.6453 26.3841 22.8285 26.6459 22.8008 26.8816C22.769 27.1456 22.5134 27.312 22.2573 27.2569C21.97 27.1951 21.8811 26.9405 21.7604 26.7137L21.4556 26.1485L20.3633 24.1251C20.2184 23.8564 19.9537 23.3278 19.7958 23.086L19.7866 23.0722C19.6155 23.2856 19.4689 23.4753 19.2891 23.6865C18.9513 24.0595 18.5732 24.394 18.1616 24.6835C17.6382 25.0512 17.0658 25.3435 16.461 25.5518C16.1232 25.6686 15.7336 25.7776 15.3986 25.9015C15.0652 26.0247 14.2478 26.0965 13.8959 26.0351C14.0533 26.4881 14.2457 26.9296 14.4112 27.3806C14.5067 27.684 14.6868 27.91 14.6254 28.2317C14.5498 28.6282 13.9251 28.5922 13.825 28.3753C13.4814 27.6305 13.2037 26.7108 12.8807 25.9657C12.6009 25.9089 12.3353 25.883 12.0425 25.8142C11.4977 25.6862 10.9862 25.5231 10.4831 25.2735C10.3228 25.1941 10.1657 25.0949 10.0079 25.0092C9.84831 24.9226 9.66931 24.8432 9.51424 24.7517C8.0607 23.8927 6.71609 22.6155 5.49441 21.4609C4.77169 20.7834 4.04155 20.1141 3.30414 19.4528C2.7733 18.9802 2.23393 18.5175 1.68631 18.0645C1.44348 17.8636 1.15574 17.694 0.948267 17.4737C0.737357 17.2532 0.583962 16.9842 0.501616 16.6903C0.362799 16.173 0.435208 15.6215 0.702914 15.1576C1.14794 14.3888 1.96782 14.044 2.82983 14.2611C4.41474 14.6542 5.46666 15.8972 6.71438 16.8506C6.9178 17.006 7.15586 17.3058 7.30861 16.956C7.30037 16.7924 7.21502 16.4656 7.17503 16.2939L6.91584 15.1839L5.70713 9.98822L5.34201 8.42268C5.27968 8.15646 5.17293 7.75527 5.14399 7.49906C5.11567 7.23596 5.1412 6.96984 5.21903 6.71691C5.53605 5.69408 6.5358 5.18312 7.55765 5.43522C7.65245 5.4586 7.74711 5.4895 7.84033 5.51861C7.81997 4.39772 8.72177 3.44846 9.82811 3.3368ZM19.4073 22.0131C19.0239 22.2243 18.5935 22.5096 18.2271 22.7504C17.8462 22.9945 17.4904 23.2851 17.1138 23.5194C16.8647 23.6743 16.4485 23.684 16.1851 23.6213C15.1493 23.3748 14.794 22.2479 15.3902 21.3973C15.7851 20.8339 16.1432 20.5456 16.7096 20.1721C17.0041 19.9628 17.3103 19.7868 17.6021 19.5864C17.2812 19.5975 16.8877 19.5604 16.5597 19.5464L14.1948 19.4315L12.7676 19.3669C12.508 19.3573 11.9105 19.3423 11.6784 19.2946C11.4532 19.2465 11.2451 19.1384 11.0763 18.9818C10.6723 18.6104 10.4935 17.9578 10.6944 17.4432C10.7204 17.3765 10.7535 17.3099 10.7847 17.2453C10.3849 17.0015 10.0878 16.6643 9.97816 16.1992C9.88379 15.8066 9.9525 15.3925 10.1686 15.0515C10.3164 14.8181 10.4847 14.6745 10.7153 14.5322C10.5425 14.1851 10.5115 13.8084 10.6354 13.439C10.7481 13.0932 10.9939 12.8066 11.3186 12.6426C11.3777 12.613 11.4408 12.585 11.5013 12.558C11.2388 11.7601 11.7054 10.9887 12.5777 10.9614C12.8954 10.9515 13.2451 10.9834 13.565 10.9947L15.609 11.0652L17.3646 11.1246C17.6448 11.1336 18.1045 11.1334 18.3717 11.1641C18.0711 9.82864 17.7088 8.41874 17.3796 7.08517L17.0918 5.9004C17.0506 5.72874 17.0208 5.55042 16.9658 5.38135C16.7048 4.57968 15.4015 4.90295 15.4622 5.69466C15.4894 6.04972 15.6155 6.49316 15.6973 6.85049L16.1935 9.01419C16.2448 9.24207 16.3444 9.557 16.3419 9.78404C16.3382 10.1192 15.9887 10.324 15.695 10.1524C15.565 10.0772 15.5151 10.0038 15.4737 9.85624C15.4129 9.60442 15.3573 9.3453 15.3001 9.09186L14.9142 7.40302C14.8444 7.09666 14.7748 6.7861 14.7024 6.48018C14.676 6.36929 14.565 6.24034 14.5315 6.12935C14.432 5.80041 14.3589 5.23199 14.2107 4.93807C14.1163 4.74945 13.9681 4.59305 13.7849 4.48866C13.1553 4.13365 12.2975 4.54168 12.2214 5.28195C12.2045 5.44686 12.3118 5.70643 12.3326 5.87547C12.4614 6.76024 12.7223 7.6221 12.9157 8.49453C13.0073 8.90799 13.1222 9.31771 13.2078 9.73291C13.2845 10.1046 13.1207 10.296 12.7714 10.397C12.6209 10.3638 12.4746 10.3245 12.3859 10.186C12.2875 10.0325 12.04 8.78649 11.9809 8.52747L11.3737 5.86821C11.3019 5.54142 11.3243 5.23881 11.1656 4.93349C10.9468 4.51263 10.5267 4.23783 10.047 4.24023C9.86577 4.24258 9.74606 4.24034 9.57108 4.30904C9.11692 4.48732 8.76893 4.95381 8.76585 5.44719C8.76435 5.68704 8.85326 5.99943 8.90859 6.2409L9.12661 7.19077L9.93506 10.7104L10.1018 11.43C10.1354 11.5726 10.2031 11.832 10.2046 11.9727C10.2064 12.0911 10.1619 12.2056 10.0805 12.2917C9.81564 12.5726 9.39056 12.4893 9.29826 12.1006C8.91556 10.4894 8.57994 8.86435 8.16463 7.26118C8.11665 7.07587 8.07821 6.8934 7.968 6.73155C7.62653 6.17113 6.79057 6.10273 6.33676 6.55951C5.85538 7.03043 6.06865 7.62599 6.20078 8.19381L6.42602 9.16074L7.69036 14.5942L7.99679 15.9049C8.07592 16.2415 8.1749 16.5729 8.19122 16.92C8.22173 17.568 7.6415 18.0515 7.01238 17.9872C6.67138 17.9523 6.54477 17.8336 6.2915 17.6199C5.92071 17.307 5.54028 17.0015 5.16381 16.6954C4.47085 16.1 3.82721 15.5284 2.94963 15.2191C2.48945 15.0569 2.03856 14.9657 1.64424 15.3451C1.21828 15.7549 1.20889 16.457 1.64312 16.8588C1.76884 16.975 1.92241 17.0857 2.05909 17.1921C2.26568 17.3525 2.46942 17.5168 2.67019 17.6845C3.33678 18.2373 3.98924 18.8068 4.62695 19.3927C5.53394 20.2142 6.41693 21.0719 7.3207 21.8844C8.24934 22.7192 9.20191 23.5294 10.3235 24.085C10.6262 24.235 10.9059 24.4631 11.2447 24.5614C12.1502 24.8986 13.048 25.0335 14.0071 25.0461C15.6784 25.0679 17.2091 24.5295 18.4072 23.3467C18.7169 23.0408 19.2221 22.4124 19.4073 22.0131ZM17.0883 18.7428C17.3556 17.7079 17.9281 16.7241 18.5119 15.8407C18.8227 15.3982 19.1558 14.9721 19.5103 14.5639C19.6472 14.4041 19.7653 14.2654 19.9209 14.1198C20.3087 13.7482 20.9069 14.2815 20.5768 14.7047C20.5294 14.7654 20.4863 14.8179 20.4315 14.8757C19.4449 15.916 18.5847 17.1345 18.0927 18.4894C18.0573 18.5867 18.0247 18.6906 17.9823 18.7836C18.2459 18.8751 18.4484 19.0092 18.5732 19.2673C18.6661 19.4596 18.6882 19.6758 18.6123 19.8784C18.5586 20.0218 18.4633 20.1388 18.3506 20.2399C17.8973 20.6462 15.7319 21.7411 16.064 22.5053C16.1145 22.6216 16.2233 22.7086 16.3406 22.752C16.4427 22.7897 16.5745 22.7873 16.6736 22.7409C16.8004 22.6816 16.9084 22.5405 17.0249 22.4574C17.4325 22.1659 17.8489 21.8866 18.2731 21.6197C18.5928 21.4219 18.9148 21.2278 19.2392 21.0378C19.4422 20.919 19.6525 20.8055 19.8471 20.6737C19.9789 20.5844 20.0329 20.4168 20.1677 20.3419C20.2927 20.2725 20.4774 20.291 20.6373 20.2043C20.8536 20.0872 21.0644 19.926 21.2699 19.7891C22.3846 19.046 23.2205 18.2754 23.6535 16.9621C23.9699 16.0024 23.9713 14.9954 23.5035 14.0826C23.0816 13.2576 22.3481 12.6349 21.4657 12.3526C21.1181 12.242 20.5963 12.1715 20.2189 12.1379C19.7155 12.0931 19.1791 12.1034 18.672 12.0838L15.2633 11.9668L13.5339 11.9068C13.2495 11.897 12.8686 11.8666 12.5948 11.8882C12.2619 12.1041 12.4348 12.4666 12.7614 12.4992C13.0904 12.5321 13.446 12.5306 13.7771 12.5453L15.7643 12.6427C16.0674 12.6563 16.3739 12.657 16.6739 12.7054C16.8793 12.7336 17.0853 13.0156 16.981 13.2074C16.8972 13.3615 16.6439 13.5908 16.4647 13.5874C15.1721 13.5625 13.8747 13.4294 12.5819 13.443C12.4026 13.4449 11.9382 13.3979 11.7924 13.4521C11.655 13.5039 11.5542 13.623 11.5258 13.767C11.468 14.0609 11.6369 14.3211 11.9422 14.3467C12.3166 14.3781 12.6913 14.3843 13.0667 14.4L15.0648 14.4966C15.3345 14.5101 15.695 14.5015 15.9542 14.5475C16.3928 14.6254 16.4644 15.1041 16.077 15.353C16 15.4021 15.912 15.4314 15.8209 15.4381C15.7144 15.4458 15.4065 15.4254 15.2891 15.4205C14.9162 15.4072 14.5433 15.3899 14.1707 15.3682C13.4656 15.3307 12.7498 15.2964 12.0452 15.3002C11.8533 15.3084 11.4419 15.2231 11.2751 15.2935C10.641 15.5611 10.7255 16.3599 11.3984 16.5006C11.6725 16.5579 12.0356 16.5338 12.322 16.5454L14.7945 16.6524C15.1325 16.6657 15.8099 16.6534 16.0969 16.7264C16.2047 16.7539 16.2937 16.8127 16.355 16.9068C16.4235 17.0118 16.4427 17.1379 16.4145 17.2599C16.3876 17.3759 16.3144 17.4734 16.2134 17.5361C16.1596 17.5696 16.093 17.5991 16.0293 17.6055C15.7741 17.6308 15.486 17.586 15.2287 17.5728C14.6654 17.5435 12.1985 17.3749 11.8361 17.521C11.6801 17.5838 11.5766 17.7289 11.5375 17.8889C11.5075 18.0118 11.5224 18.1388 11.591 18.2463C11.6832 18.3906 11.8514 18.4696 12.015 18.5002C12.2388 18.5418 12.4942 18.5296 12.7224 18.539L13.9322 18.5949L16.365 18.7086C16.5973 18.7184 16.8609 18.7385 17.0883 18.7428Z"
        fill={ACTIVE_ICON_COLOR}
      />
      <path
        d="M24.2961 2.2463C24.3752 2.15497 24.4586 2.06746 24.546 1.98404C25.087 1.47086 25.8115 1.19666 26.5568 1.22306C27.3034 1.25298 28.0079 1.57696 28.5165 2.12431C29.0382 2.67738 29.3121 3.41895 29.2754 4.1783C29.1996 5.83321 27.2907 7.71572 26.1364 8.77068C25.902 8.98142 24.4926 10.2353 24.304 10.2865C24.1257 10.2658 23.6813 9.85286 23.522 9.72299C22.0561 8.52809 19.4948 6.26714 19.3024 4.29416C19.2295 3.54764 19.4838 2.80358 19.958 2.22917C20.4506 1.63273 21.1455 1.30222 21.908 1.22797C21.9801 1.22385 22.0523 1.22164 22.1246 1.22134C22.7736 1.22311 23.4017 1.45101 23.901 1.86584C24.0361 1.97703 24.1708 2.12076 24.2961 2.2463ZM26.2984 7.49592C27.0889 6.70973 28.3866 5.33922 28.4435 4.15625C28.4491 4.03678 28.4052 3.88671 28.3783 3.77171C28.2847 3.37195 28.1249 3.03126 27.8293 2.73739C27.4544 2.36469 26.9883 2.17588 26.4583 2.17843C25.9285 2.17086 25.4295 2.3796 25.0596 2.75868C24.9985 2.82128 24.9011 2.9282 24.8537 3.0154C24.6979 3.30167 24.4347 3.79895 24.0536 3.46939C23.9436 3.37432 23.84 3.13228 23.7461 2.99697C23.3991 2.48891 22.8248 2.18349 22.2095 2.17973C22.1367 2.17933 22.0709 2.18011 21.9983 2.18734C21.457 2.23995 21.0126 2.44111 20.6615 2.86967C20.3329 3.26786 20.1761 3.78047 20.2258 4.29441C20.3866 5.8798 23.0197 8.17326 24.1996 9.14396C24.2434 9.1799 24.2821 9.2078 24.3371 9.22273C24.5351 9.14212 26.0985 7.70157 26.2984 7.49592Z"
        fill={ACTIVE_ICON_COLOR}
      />
    </g>
    <defs>
      <clipPath id="family-details-note-icon">
        <rect width="29.7933" height="29.7933" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="20"
    height="23"
    viewBox="0 0 20 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.10474 0H12.7672C12.8405 0.024528 12.915 0.0314782 12.9906 0.0466206C13.5752 0.163704 14.0686 0.541957 14.3487 1.06603C14.6168 1.56753 14.5892 2.09976 14.5829 2.65007L16.8309 2.6522C17.2119 2.65186 17.9946 2.62676 18.338 2.70744C18.7022 2.79197 19.0358 2.97635 19.3012 3.23989C19.6761 3.61643 19.8851 4.1271 19.8817 4.65847C19.8786 5.19075 19.6602 5.69909 19.2759 6.06748C19.0229 6.3108 18.8376 6.39524 18.5267 6.53121C18.4988 6.65982 18.4913 6.82658 18.4805 6.96034L18.425 7.65265L18.2274 10.0319L17.5553 18.1436L17.404 19.967C17.3758 20.3093 17.3469 20.8908 17.2534 21.203C17.1138 21.6642 16.8137 22.06 16.4073 22.3187C16.2327 22.431 16.0325 22.5246 15.8298 22.568C15.7512 22.5849 15.6216 22.6096 15.553 22.633H4.34019C4.29738 22.618 4.21505 22.6007 4.16837 22.5932C3.45661 22.4779 2.78007 21.8655 2.61297 21.1541C2.51497 20.7369 2.49385 20.1733 2.45644 19.7212L2.25203 17.2488L1.66281 10.1344L1.4704 7.81019C1.4362 7.39775 1.40853 6.94253 1.3607 6.53479C1.02094 6.37442 0.886455 6.34406 0.594404 6.05842C0.217512 5.69394 0.00330544 5.19303 0.00013373 4.66875C-0.00609918 4.12939 0.205775 3.61039 0.587685 3.2295C1.28814 2.53015 2.12416 2.65217 3.02327 2.65225L5.2976 2.65131C5.30341 2.10104 5.25556 1.62098 5.51037 1.10965C5.7321 0.664692 6.14022 0.27532 6.61771 0.119655C6.71671 0.0874847 6.81738 0.0607206 6.91928 0.0394808C6.98305 0.0267311 7.04287 0.0217848 7.10474 0ZM16.0538 20.2022L16.8644 10.4042L17.0639 7.99776C17.1012 7.54811 17.1456 7.08431 17.1741 6.63502L7.317 6.63421L4.17879 6.63429L3.19103 6.63489C3.05887 6.63507 2.83791 6.64454 2.71613 6.63508C2.69757 6.68009 2.96207 9.75062 2.98641 10.0435L3.6427 17.9625L3.79271 19.7666C3.81423 20.023 3.835 20.6897 3.92152 20.8978C3.98577 21.0523 4.11668 21.1867 4.27195 21.2502C4.35658 21.2848 4.44846 21.2995 4.5394 21.3034C4.75107 21.3123 4.96474 21.3069 5.17659 21.3069L6.29298 21.3064H9.65339L13.3935 21.3065H14.6158C14.8573 21.3066 15.4123 21.3361 15.613 21.2469C15.7786 21.1732 15.9073 21.0354 15.9695 20.8651C16.0265 20.7065 16.0368 20.3793 16.0538 20.2022ZM2.0309 3.98328C1.8049 4.0005 1.63762 4.0284 1.47994 4.21347C1.36524 4.3505 1.30999 4.52767 1.32649 4.7056C1.34373 4.88093 1.43028 5.04211 1.56689 5.15335C1.69856 5.26073 1.82744 5.29336 1.99412 5.30003C2.20801 5.30858 2.43209 5.30458 2.64658 5.30461L3.86773 5.30456L7.61715 5.3041L14.3851 5.3042C15.5395 5.3042 16.7161 5.31754 17.8672 5.30292C18.0856 5.28707 18.2478 5.24105 18.3972 5.06942C18.511 4.93802 18.5675 4.76649 18.554 4.59317C18.5416 4.41536 18.4573 4.25027 18.3207 4.13578C18.184 4.02056 18.0561 3.9935 17.8816 3.98693C17.6338 3.97757 17.3765 3.98166 17.128 3.98162L15.7453 3.98163L11.4117 3.98211L5.1677 3.98193L3.10741 3.9819C2.76218 3.9819 2.37196 3.97299 2.0309 3.98328ZM6.62601 2.65008L12.5546 2.65181C12.7841 2.65179 13.0261 2.65533 13.2545 2.65132C13.2614 1.87245 13.3611 1.33311 12.3724 1.32911L7.33667 1.33096C6.494 1.39407 6.63181 2.02228 6.62601 2.65008Z"
      fill="#444444"
    />
    <path
      d="M5.91692 7.96113C6.29712 7.94773 6.5957 8.20435 6.62083 8.58289C6.63507 8.7974 6.65204 9.01213 6.66548 9.227L6.73997 10.4337L6.97463 14.2221L7.18662 17.642L7.25353 18.7136C7.26534 18.9045 7.27794 19.0933 7.28446 19.2843C7.29721 19.6578 7.04188 19.936 6.67328 19.9782C6.32868 19.9903 6.02069 19.776 5.97705 19.4225C5.94939 19.1984 5.93942 18.9751 5.92592 18.75L5.85988 17.682L5.64281 14.1764L5.41045 10.4315L5.34259 9.33105C5.3279 9.10562 5.30932 8.88257 5.30579 8.65641C5.29957 8.25796 5.52922 8.01472 5.91692 7.96113Z"
      fill="#444444"
    />
    <path
      d="M13.8779 7.96097C14.0351 7.95884 14.1767 7.99144 14.3064 8.08468C14.4518 8.18751 14.548 8.34602 14.5721 8.52244C14.5905 8.65289 14.5702 8.85066 14.5617 8.98964L14.5208 9.64714L14.3688 12.0996L14.0703 16.915L13.971 18.5042C13.9539 18.783 13.9419 19.0623 13.9182 19.3408C13.8876 19.7008 13.6575 19.9329 13.3031 19.9793C13.1377 19.9844 12.989 19.9455 12.8552 19.8457C12.7195 19.7455 12.6295 19.595 12.6054 19.428C12.5864 19.2957 12.6072 19.071 12.6157 18.9297L12.6601 18.2126L12.8194 15.6494L13.1079 10.9964L13.2062 9.4092C13.224 9.12645 13.2315 8.83947 13.2635 8.55832C13.3041 8.20131 13.5336 8.00565 13.8779 7.96097Z"
      fill="#444444"
    />
    <path
      d="M9.90412 7.96053C10.0646 7.95434 10.2223 8.00456 10.3496 8.10245C10.6595 8.34244 10.6045 8.70561 10.6047 9.05557V9.94489L10.6045 12.8976V17.1816L10.6047 18.5655C10.6047 18.8138 10.6079 19.0618 10.6011 19.31C10.5912 19.6838 10.3454 19.9363 9.98137 19.9793C9.68354 19.9809 9.44095 19.8447 9.32402 19.5642L9.31561 19.5438C9.26455 19.4214 9.27797 19.0092 9.27794 18.8523L9.27782 17.9557L9.2777 14.9872L9.27795 10.74L9.27782 9.3743C9.2777 9.12386 9.27487 8.87444 9.2825 8.62409C9.2944 8.23372 9.52753 8.00648 9.90412 7.96053Z"
      fill="#444444"
    />
  </svg>
);

const getDateParts = (value: string): DateParts => {
  const [year = '', month = ''] = value.split('-');

  return { month, year };
};

const getBirthDate = ({ month, year }: DateParts) =>
  month && year ? `${year}-${month}` : '';

const getBirthMonth = (
  month: string,
  months: { label: string; value: string }[]
) => {
  return months.find(({ value }) => value === month)?.label || '';
};

const getBirthYear = (value: string) => value.split('-')[0] || '';

export const FamilyDetailsPopup = ({
  value,
  onChange,
  onClose
}: FamilyDetailsPopupProps) => {
  const {
    title,
    description,
    adultsLabel,
    childrenLabel,
    skipAges,
    birthDateLabel,
    birthMonthLabel,
    birthYearLabel,
    birthDatePlaceholder,
    button
  } = useTranslation(Content) as Record<keyof typeof Content, string>;
  const t = useDashboardTranslation();
  const [childBirthDateParts, setChildBirthDateParts] = useState<DateParts[]>(
    () => value.childBirthDates.map(getDateParts)
  );

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const month = index + 1;

        return {
          label: new Intl.DateTimeFormat(undefined, { month: 'long' }).format(
            new Date(2000, index, 1)
          ),
          value: String(month).padStart(2, '0')
        };
      }),
    []
  );

  const years = useMemo(
    () =>
      Array.from({ length: 19 }, (_, index) => String(CURRENT_YEAR - index)),
    []
  );

  const copy = {
    title: t('Family_onboarding_family_size_title', title),
    description: t('Family_onboarding_family_size_description', description),
    adultsLabel: t('Family_onboarding_family_size_adults', adultsLabel),
    childrenLabel: t('Family_onboarding_family_size_children', childrenLabel),
    skipAges: t('Family_onboarding_family_size_skip_ages', skipAges),
    birthDateLabel: t(
      'Family_onboarding_family_size_birth_date',
      birthDateLabel
    ),
    birthMonthLabel: t(
      'Family_onboarding_family_size_birth_month',
      birthMonthLabel
    ),
    birthYearLabel: t(
      'Family_onboarding_family_size_birth_year',
      birthYearLabel
    ),
    birthDatePlaceholder: t(
      'Family_onboarding_family_size_birth_date_placeholder',
      birthDatePlaceholder
    ),
    button: t('Family_onboarding_family_size_cta', button)
  };

  const updateAdults = (adults: number) => {
    onChange({
      ...value,
      adults: Math.min(Math.max(adults, 0), MAX_MEMBERS - value.children)
    });
  };

  const updateChildren = (children: number) => {
    const nextChildren = Math.min(
      Math.max(children, 0),
      MAX_MEMBERS - value.adults
    );

    onChange({
      ...value,
      children: nextChildren,
      childBirthDates: value.childBirthDates.slice(0, nextChildren)
    });
  };

  const updateChildBirthDate = (index: number, birthDate: string) => {
    const childBirthDates = [...value.childBirthDates];
    childBirthDates[index] = birthDate;

    onChange({ ...value, childBirthDates });
  };

  const updateChildBirthDatePart = (
    index: number,
    key: keyof DateParts,
    partValue: string
  ) => {
    const nextParts = [...childBirthDateParts];
    const currentParts =
      nextParts[index] || getDateParts(value.childBirthDates[index] ?? '');

    nextParts[index] = { ...currentParts, [key]: partValue };
    setChildBirthDateParts(nextParts);
    updateChildBirthDate(index, getBirthDate(nextParts[index]));
  };

  const clearChildBirthDate = (index: number) => {
    const nextParts = [...childBirthDateParts];
    nextParts[index] = { month: '', year: '' };
    setChildBirthDateParts(nextParts);

    const childBirthDates = [...value.childBirthDates];
    childBirthDates[index] = '';

    onChange({ ...value, childBirthDates });
  };

  useEffect(() => {
    setChildBirthDateParts((currentParts) =>
      Array.from({ length: value.children }, (_, index) => {
        const selectedDate = value.childBirthDates[index];

        return selectedDate
          ? getDateParts(selectedDate)
          : currentParts[index] || { month: '', year: '' };
      })
    );
  }, [value.children, value.childBirthDates]);

  return (
    <Popup
      onClose={onClose}
      width={90}
      hideClose
      customStyle={{
        minHeight: 'auto',
        padding: '30px 24px 27px',
        borderRadius: '16px',
        border: 'none',
        boxShadow: '0 -4px 4px rgb(219 13 13 / 0.25)'
      }}
    >
      <PopupContent>
        <Title>{copy.title}</Title>
        <Description>{copy.description}</Description>

        <FieldGroup>
          <FieldLabel>{copy.adultsLabel}</FieldLabel>
          <Stepper>
            <StepperButton
              type="button"
              disabled={value.adults <= 0}
              onClick={() => updateAdults(value.adults - 1)}
            >
              <MinusIcon active={value.adults > 0} />
            </StepperButton>
            <StepperValue>{value.adults}</StepperValue>
            <StepperButton
              type="button"
              disabled={value.adults + value.children >= MAX_MEMBERS}
              onClick={() => updateAdults(value.adults + 1)}
            >
              <PlusIcon />
            </StepperButton>
          </Stepper>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>{copy.childrenLabel}</FieldLabel>
          <Stepper>
            <StepperButton
              type="button"
              disabled={value.children <= 0}
              onClick={() => updateChildren(value.children - 1)}
            >
              <MinusIcon active={value.children > 0} />
            </StepperButton>
            <StepperValue>{value.children}</StepperValue>
            <StepperButton
              type="button"
              disabled={value.adults + value.children >= MAX_MEMBERS}
              onClick={() => updateChildren(value.children + 1)}
            >
              <PlusIcon />
            </StepperButton>
          </Stepper>
        </FieldGroup>

        {!!value.children && (
          <>
            <Note>
              <NoteIcon aria-hidden="true">
                <NoteHandIcon />
              </NoteIcon>
              <span>{copy.skipAges}</span>
            </Note>

            <DateFields>
              {Array.from({ length: value.children }, (_, index) => {
                const selectedDate = value.childBirthDates[index] ?? '';
                const selectedDateParts =
                  childBirthDateParts[index] || getDateParts(selectedDate);

                return (
                  <DateRow key={index}>
                    <DateColumn>
                      <DateLabel>
                        {copy.birthMonthLabel || copy.birthDateLabel}
                      </DateLabel>
                      <DateSelect>
                        <span>
                          {getBirthMonth(
                            selectedDateParts.month,
                            months
                          ) ||
                            copy.birthMonthLabel}
                        </span>
                        <SelectChevronIcon />
                        <DateInput
                          aria-label={`${copy.birthDateLabel} ${index + 1}`}
                          value={selectedDateParts.month}
                          onChange={({ target }) =>
                            updateChildBirthDatePart(
                              index,
                              'month',
                              target.value
                            )
                          }
                        >
                          <option value="">{copy.birthMonthLabel}</option>
                          {months.map(({ label, value }) => (
                            <option value={value} key={value}>
                              {label}
                            </option>
                          ))}
                        </DateInput>
                      </DateSelect>
                    </DateColumn>
                    <DateColumn>
                      <DateLabel>
                        {copy.birthYearLabel || copy.birthDateLabel}
                      </DateLabel>
                      <DateSelect>
                        <span>
                          {getBirthYear(selectedDateParts.year) ||
                            copy.birthYearLabel}
                        </span>
                        <SelectChevronIcon />
                        <DateInput
                          aria-label={`${copy.birthDateLabel} ${index + 1}`}
                          value={selectedDateParts.year}
                          onChange={({ target }) =>
                            updateChildBirthDatePart(
                              index,
                              'year',
                              target.value
                            )
                          }
                        >
                          <option value="">{copy.birthYearLabel}</option>
                          {years.map((year) => (
                            <option value={year} key={year}>
                              {year}
                            </option>
                          ))}
                        </DateInput>
                      </DateSelect>
                    </DateColumn>
                    <DeleteDateButton
                      type="button"
                      aria-label="Clear birth date"
                      onClick={() => clearChildBirthDate(index)}
                    >
                      <DeleteIcon />
                    </DeleteDateButton>
                  </DateRow>
                );
              })}
            </DateFields>
          </>
        )}

        <Button type="button" onClick={onClose}>
          {copy.button}
          <Arrow />
        </Button>
      </PopupContent>
    </Popup>
  );
};
